/* eslint-disable react-hooks/exhaustive-deps */
import React, {useContext, useEffect, useState, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { makeStyles, 
    Theme, 
    TextField, 
    MenuItem, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogContentText, 
    DialogTitle,
    List,
    ListItem,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    Checkbox,
    IconButton } from '@material-ui/core';

import { purple } from '@material-ui/core/colors';

import { AppContext } from '../stores/AppStore';
import { UserContext } from '../stores/UserStore';
import { SecurityContext } from '../stores/SecurityStore';

import PageContainer from '../components/layout/PageContainer';
import PageToolbar from '../components/layout/PageToolbar';
import PageBody from '../components/layout/PageBody';

import DataTable from '../components/table/DataTable';
import Button from '../components/common/Button';

import AddIcon from '@material-ui/icons/AddBox';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import SettingsIcon from '@material-ui/icons/Settings';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RefreshIcon from '@material-ui/icons/Refresh';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        // padding: theme.spacing(2),
    },
    dialogRoot: {
        '& .MuiPaper-root': {
            backgroundColor: theme.palette.text.primary,
        }
    },
    dialogTitle: {
        padding: '10px 24px 0px 24px',
        color: '#00000094'
    },
    dialogContent: {
        minHeight: '50vh',
        color: '#00000094',
        // padding: '20px',
        // display: 'flex',
        // alignItems: 'flex-start',
        // justifyContent: 'flex-start',
        // flexDirection: 'column'
    },
    textField: {
        width:  'calc(100% - 20px)',
        margin: theme.spacing(3,1,0,0),
        fontSize: 12,
        '& .MuiFormLabel-root': {
            color: '#666666'
        },
        '& .MuiInput-root': {
            // color: theme.palette.primary.main,
            color: '#000000',
            '& .MuiInputBase-input': {
                paddingLeft: '10px',
                backgroundColor: '#FFFFFF',
                height: '1.8em',
                borderRadius: '4px',
                boxShadow: 'inset 0 1px 3px 0 rgba(0,0,0,0.14)'
            },
            '& .MuiSelect-select.MuiSelect-select': {
                paddingTop: '10px',
                paddingBottom: '0px'
            },
            '& .Mui-disabled': {
                opacity: 0.5
            }                
        }
    },
    row: {
        display: 'flex', 
        flexDirection: 'row'
    },
    list: {
        width: 'calc(100% - 0px)',
        backgroundColor: theme.palette.text.primary,
    },         
    listItem: {
        margin: '0px',
        padding: '5px 0px'
    },
    checkbox: {
        margin: '0px',
        padding: '0px'
    },
    listItemText: {
        margin: '0px',
        padding: '0px'
    },
    dialogActions: {
        '& .MuiButton-contained.Mui-disabled': {
            color: theme.palette.grey[300],
            border: '1px solid' 
        }
    },
    buttonPurple: {
        color: theme.palette.getContrastText(purple[500]),
        backgroundColor: purple[500],
        '&:hover': {
            backgroundColor: purple[700],
        },
    }         
}));


const ManageProfiles = () => {
    const history = useHistory();    
    const { appId }: any = useParams();      
    const classes = useStyles();
    const { applicationSearchText, setApplicationSearchText, showSuccessMessage, showErrorMessage } = useContext(AppContext);
    const { currentUser } = useContext(UserContext);
    const { profilesData, profilesDashboard } = useContext(SecurityContext)
    const [isLoading, setLoading] = useState(true);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [currentSettings, setCurrentSettings] = useState(null);
    const [ownerProfiles, setOwnerProfiles] = useState([]);
    const [listRoles, setListRoles] = useState([]);
    const [action, setAction] = useState(null);

    useEffect(() =>  {
        if (applicationSearchText) {
            setApplicationSearchText('');
            history.push('/search/users/'+applicationSearchText);
        }
    }, [applicationSearchText]);

    useEffect(() =>  {
        init();
    }, [appId]);

    const init = () => {
        setLoading(true);        
        profilesDashboard({applicationId: appId, init: true}).catch(errorMessage => {
            showErrorMessage(errorMessage);
        }).finally(() => {
            setLoading(false);            
        });
    };

    const handleClickNew = () => {
        const record = {applicationId: profilesData.applicationData.applications[0].id};
        loadApplicationSettings(record, 'New');
        setCurrentRecord(record);
        setAction('New');
    };

    const handleClickEdit = (event, rowData) => {
        const record = profilesData.tableData.find(x => x.id === rowData.id);
        loadApplicationSettings(rowData, 'Edit');
        setCurrentRecord(record);
        setAction('Edit');
    };    

    const handleClickDelete = (event, rowData) => {
        setAction('Delete');
        setCurrentRecord(rowData);
    };    

    const handleClickClose = () => {
        setAction(null);
        setOwnerProfiles([]);
        setListRoles([]);
        setCurrentRecord(null);        
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        const record = {...currentRecord};
        record[event.target.name] = event.target.value;
        setCurrentRecord(record);
    };        

    const handleApplicationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        const record = {...currentRecord};
        record.applicationId = event.target.value;
        loadApplicationSettings(record, action);
        setCurrentRecord(record);
    };      

    const handleProfileOwnerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        const record = {...currentRecord};
        record.ownerId = event.target.value;
        loadApplicationProfiles(record, ownerProfiles, action);
        setCurrentRecord(record);
    };  

    const doClickDelete = () => {
        setLoading(true);            
        profilesDashboard({ applicationId: appId, delete: currentRecord.id }).then(() => {
            showSuccessMessage('Profile delete success!');
        }).catch(errorMessage => {
            showErrorMessage(errorMessage);
        }).finally(() => {
            setAction(null);
            setCurrentRecord(null);
            setLoading(false);            
        });
    };      

    const doClickSave = () => {
        setLoading(true);
        const saveRecord = {
            id: currentRecord.id,
            name: currentRecord.name,
            label: currentRecord.label,
            application: currentRecord.applicationId,
            owner: currentRecord.ownerId,
            roles: []
        };
        loadRolesSelected(saveRecord.roles, listRoles);
        profilesDashboard({ applicationId: appId, save: saveRecord }).then(() => {
            showSuccessMessage('Profile saved success!');
        }).catch(errorMessage => {
            showErrorMessage(errorMessage);
        }).finally(() => {
            setAction(null);
            setCurrentRecord(null);
            setLoading(false);            
        });
    };      

    const loadApplicationSettings = (record, action) => {
        setListRoles([]); 
        
        const ownerProfiles = JSON.parse(JSON.stringify(profilesData.tableData.filter(x => x.applicationId === record.applicationId)));
        
        if (action === 'New' && ownerProfiles.length > 0) {
            record.ownerId = ownerProfiles[0].id;
        }
        
        loadApplicationProfiles(record, ownerProfiles, action);
        setOwnerProfiles(ownerProfiles);
    }  

    const loadApplicationProfiles = (record, ownerProfiles, action) => {
        setListRoles([]); 
    
        let obj = ownerProfiles.find(x => x.id === record.ownerId);
        if (action === 'Edit' && record.id === record.ownerId) {
          obj = profilesData.applicationData.settings.find(x => x.id === record.applicationId);
        }
        
        const listRoles = [];
        const settings = obj ? JSON.parse(JSON.stringify(obj)).settings : [];
        for (const obj of settings) {
          const newObj = JSON.parse(JSON.stringify(obj));
          newObj.description = newObj.description ? newObj.description : newObj.name;
          const objExists = listRoles.find(x => x.id === newObj.id);
          if (!objExists) {
            listRoles.push(newObj);  
          } 
        }
        if (action === 'Edit') {
            loadRoles(listRoles, record.roles);
        }
        setListRoles(listRoles);
    }  

    const handleCheckRoles = (rowData: any) => () => {
        const list = [...listRoles];
        const role = list.find(x => x.id === rowData.id);
        role.checked = !role.checked;
        setListRoles(list);
    };
    
    const handleCheckSettings = (rowData: any) => () => {
        const newSettings = {...currentSettings};
        const role = newSettings.settings.find(x => x.id === rowData.id);
        role.checked = !role.checked;
        setCurrentSettings(newSettings);        
    };

    const handleClickSettings = (role: any) => () => {
        setCurrentSettings(role);
    };        

    const handleClickSettingsClose = () => {
        const rolesChecked = currentSettings.settings.filter(x => x.checked);
        if (!currentSettings.owner) {
            const role = listRoles.find(x => x.id === currentSettings.id);
            if (!role.checked && rolesChecked.length > 0) {
                role.checked = true;                
            }
            setListRoles(listRoles); 
            setCurrentSettings(null);   
        } else {
            const newSettings = {...currentSettings.owner};
            const role = newSettings.settings.find(x => x.id === currentSettings.id);
            if (!role.checked && rolesChecked.length > 0) {
                role.checked = true;                
            }
            setCurrentSettings(newSettings);
        }
    };

    const doClickSettingsFullAccess = () => {
        const newSettings = {...currentSettings};
        loadSettingsFullAccess(currentSettings.settings)
        setCurrentSettings(newSettings);        
    };      

    const loadSettingsFullAccess = (settings: any[]) => {
        for (const obj of settings) {
            obj.checked = true;
            if (obj.settings.length > 0) {
                loadSettingsFullAccess(obj.settings);
            }
        }
    };

    const loadRolesSelected = (roles, rolesSelected) => {
        for (const roleSelected of rolesSelected) {
            if (roleSelected.checked) {
                const role = { id: roleSelected.id, settings: [] };
                roles.push(role)
                if (roleSelected.settings && roleSelected.settings.length > 0) {
                    loadRolesSelected(role.settings, roleSelected.settings);
                }      
            }
        }
    }    

    const loadRoles = (roles, rolesSaved) => {
        for (const role of roles) {
          const roleSaved = rolesSaved.find(x => x.id === role.id);
          if (roleSaved) {    
            role.checked = true;
            if (roleSaved.settings && roleSaved.settings.length > 0) {
              loadRoles(role.settings, roleSaved.settings);
            }
          }  
        }
    }    
    
    const doClickRolesFullAccess = () => {
        const list = [...listRoles];
        loadSettingsFullAccess(list);
        setListRoles(list);        
    };      
    
    const renderTableData = useMemo(() => <DataTable
        isLoading={isLoading}
        columns={[
            { title: 'Name', field: 'name' },
            { title: 'Application', field: 'applicationName' }
        ]}
        data={JSON.parse(JSON.stringify(profilesData.tableData))}        
        actions={[
            {
                icon: () => <EditIcon color="primary" />,
                tooltip: 'Edit Profile',
                onClick: handleClickEdit,
                hidden: !(currentUser.isSuperAdmin || currentUser.isMaintainer)
            },
            {
                icon: () => <DeleteIcon color="error" />,
                tooltip: 'Delete Profile',
                onClick: handleClickDelete,
                hidden: !(currentUser.isSuperAdmin || currentUser.isMaintainer)
            },
            {
                icon: () => <RefreshIcon color="primary" />,
                tooltip: 'Refresh',
                onClick: (event) => init(),
                isFreeAction: true
            }
        ]}              
    />, [profilesData, isLoading]);

    const renderListRoles = useMemo(() => <List dense={true} className={classes.list}>
        {listRoles.map((role) => {
            const labelId = `roles-list-label-${role.id}`;
            role.owner = null;
            return (
                <ListItem key={role.id} className={classes.listItem} role={undefined} dense={true} button onClick={handleCheckRoles(role)}>
                    <ListItemIcon>
                        <Checkbox
                            className={classes.checkbox}
                            edge="start"
                            checked={role.checked ? role.checked : false}
                            tabIndex={-1}
                            inputProps={{ 'aria-labelledby': labelId }}
                        />
                    </ListItemIcon>
                    <ListItemText id={labelId} className={classes.listItemText} primary={role.description} />
                    {role.settings && role.settings.length > 0 && <ListItemSecondaryAction>
                        <IconButton edge="end" aria-label="roles" onClick={handleClickSettings(role)}>
                            <SettingsIcon color="secondary" />
                        </IconButton>
                    </ListItemSecondaryAction>}
                </ListItem>
            );
        
        })}
    </List>, [listRoles, currentSettings]);

    const renderListSettings = useMemo(() => <List dense={true} className={classes.list}>
        {currentSettings && currentSettings.settings && currentSettings.settings.map((role) => {
            const labelId = `settigns-list-label-${role.id}`;
            role.owner = currentSettings;
            return (
                <ListItem key={role.id} className={classes.listItem} role={undefined} dense={true} button onClick={handleCheckSettings(role)}>
                    <ListItemIcon>
                        <Checkbox
                            className={classes.checkbox}
                            edge="start"
                            checked={role.checked ? role.checked : false}
                            tabIndex={-1}
                            inputProps={{ 'aria-labelledby': labelId }}
                        />
                    </ListItemIcon>
                    <ListItemText id={labelId}  className={classes.listItemText} primary={role.description} />
                    {role.settings && role.settings.length > 0 && <ListItemSecondaryAction>
                        <IconButton edge="end" aria-label="settings" onClick={handleClickSettings(role)}>
                            <SettingsIcon color="secondary"/>
                        </IconButton>
                    </ListItemSecondaryAction>}
                </ListItem>
            );
        
        })}
    </List>, [currentSettings]);

    return (
        <PageContainer className={classes.root}>
            <PageToolbar title="Profiles Management" backButton={true}>
                { (currentUser.isSuperAdmin || currentUser.isMaintainer) &&
                    <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={handleClickNew} >
                        New Profile
                    </Button>
                }
            </PageToolbar>
            <PageBody>
                { profilesData.tableData && renderTableData }
            </PageBody>
            <Dialog
                className={classes.dialogRoot}
                disableBackdropClick
                disableEscapeKeyDown            
                fullWidth
                maxWidth="md"
                open={action === 'New' || action === 'Edit'}
                aria-labelledby="save-dialog">
                <DialogTitle className={classes.dialogTitle} id="save-dialog">{action} Profile</DialogTitle>
                <DialogContent dividers={false} className={classes.dialogContent}>
                    { currentRecord && <>
                        <div className={classes.row}>                        
                            <TextField
                                label="Name:"
                                required
                                placeholder="Name"
                                id="name"
                                name="name"
                                className={classes.textField}
                                error={currentRecord.name !== undefined && !currentRecord.name}
                                value={currentRecord.name}
                                onChange={handleInputChange}                            
                                fullWidth
                                margin="dense"
                                InputLabelProps={{shrink: true}}
                                />

                            <TextField
                                required
                                label="Label:"
                                placeholder="Label"
                                id="label"
                                name="label"
                                className={classes.textField}
                                error={currentRecord.label !== undefined && !currentRecord.label}
                                value={currentRecord.label}
                                onChange={handleInputChange}                            
                                fullWidth
                                margin="dense"
                                inputProps={{ maxLength: 15 }}
                                InputLabelProps={{shrink: true}}
                            />                            

                        </div>
                        <div className={classes.row}>                        
                            <TextField
                                select
                                label="Application:"
                                required
                                disabled={action === 'Edit'}
                                placeholder="Application"
                                id="applicationId"
                                name="applicationId"
                                className={classes.textField}
                                error={currentRecord.applicationId !== undefined && !currentRecord.applicationId}
                                value={currentRecord.applicationId}
                                onChange={handleApplicationChange}                            
                                fullWidth
                                margin="dense"
                                InputLabelProps={{shrink: true}}>
                                { 
                                    profilesData && profilesData.applicationData && profilesData.applicationData.applications && profilesData.applicationData.applications.map((application) => (
                                        <MenuItem key={application.id} value={application.id}>
                                            {application.description}
                                        </MenuItem>
                                    ))
                                }
                            </TextField>

                            <TextField
                                select
                                label="Profile Owner:"
                                required
                                disabled={action === 'Edit'}
                                placeholder="Profile Owner"
                                id="ownerId"
                                name="ownerId"
                                className={classes.textField}
                                error={currentRecord.ownerId !== undefined && !currentRecord.ownerId}
                                value={currentRecord.ownerId}
                                onChange={handleProfileOwnerChange}                            
                                fullWidth
                                margin="dense"
                                InputLabelProps={{shrink: true}}>
                                { 
                                    ownerProfiles.map((profile) => (
                                        <MenuItem key={profile.id} value={profile.id}>
                                            {profile.name}
                                        </MenuItem>
                                    ))
                                }
                            </TextField>
                        </div>

                        {renderListRoles}

                    </> }
                </DialogContent>
                <DialogActions className={classes.dialogActions} >
                    <Button  variant="contained" className={classes.buttonPurple} startIcon={<DoneAllIcon />} onClick={doClickRolesFullAccess} >
                        Full Access
                    </Button>
                    <div style={{flex: '1 0'}}></div>
                    <Button variant="contained" color="secondary" startIcon={<CloseIcon />} onClick={handleClickClose}>
                        Close
                    </Button>
                    <Button disabled={currentRecord && (!currentRecord.name || !currentRecord.applicationId || !currentRecord.ownerId)} variant="contained" color="primary" startIcon={<SaveIcon />} onClick={doClickSave} >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                className={classes.dialogRoot}
                disableBackdropClick
                disableEscapeKeyDown            
                fullWidth
                maxWidth="sm"
                open={action === 'Delete'}
                aria-labelledby="delete-dialog">
                <DialogTitle className={classes.dialogTitle} id="delete-dialog">{action} Profile</DialogTitle>
                <DialogContent dividers={false}>
                    <DialogContentText>
                        Do you want delete profile "{currentRecord && currentRecord.name}"? 
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="secondary" startIcon={<CloseIcon />} onClick={handleClickClose}>
                        Close
                    </Button>
                    <Button variant="contained" color="primary" startIcon={<DeleteIcon />} onClick={doClickDelete} >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                className={classes.dialogRoot}
                disableBackdropClick
                disableEscapeKeyDown            
                fullWidth
                maxWidth="md"
                open={currentSettings !== null}
                aria-labelledby="settings-dialog">
                <DialogTitle className={classes.dialogTitle} id="settings-dialog">
                    <IconButton color="primary" component="span" onClick={handleClickSettingsClose}>
                        <ArrowBackIcon />
                    </IconButton> 
                    Edit Settings: {currentSettings && currentSettings.description}
                </DialogTitle>
                <DialogContent dividers={false} className={classes.dialogContent}>
                    { renderListSettings }
                </DialogContent>
                <DialogActions>
                    <Button  variant="contained" className={classes.buttonPurple} startIcon={<DoneAllIcon />} onClick={doClickSettingsFullAccess} >
                        Full Access
                    </Button>
                    {/* <div style={{flex: '1 0'}}></div>
                    <Button variant="contained" color="secondary" startIcon={<CloseIcon />} onClick={handleClickSettingsClose}>
                        Close
                    </Button> */}
                </DialogActions>
            </Dialog>

        </PageContainer>
    );
};

export default ManageProfiles;
